import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2, 
  Moon, 
  Brain, 
  Heart, 
  Flame,
  X,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { format, subDays, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface WelcomeBackSummaryProps {
  userId: string;
  onDismiss?: () => void;
}

interface YesterdaySummary {
  checkInCompleted: boolean;
  mood: string | null;
  sleepHours: number | null;
  sleepQuality: number | null;
  therapySessions: number;
  meditationMinutes: number;
  streakCount: number;
  topActivities: string[];
}

const MOOD_LABELS: Record<string, { label: string; emoji: string }> = {
  amazing: { label: 'Amazing', emoji: '✨' },
  good: { label: 'Good', emoji: '😊' },
  okay: { label: 'Okay', emoji: '😐' },
  struggling: { label: 'Struggling', emoji: '😔' },
  crisis: { label: 'In crisis', emoji: '🆘' },
};

export const WelcomeBackSummary: React.FC<WelcomeBackSummaryProps> = ({ 
  userId, 
  onDismiss 
}) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = React.useState(false);

  // Check if already shown today
  const storageKey = `welcome_back_shown_${format(new Date(), 'yyyy-MM-dd')}`;
  const alreadyShown = localStorage.getItem(storageKey) === 'true';

  const { data: summary, isLoading } = useQuery({
    queryKey: ['yesterday-summary', userId],
    queryFn: async (): Promise<YesterdaySummary> => {
      const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
      
      // Get yesterday's check-in
      const { data: checkIn } = await supabase
        .from('daily_checkins')
        .select('daily_feeling, hours_slept, sleep_quality')
        .eq('user_id', userId)
        .eq('date', yesterday)
        .maybeSingle();

      // Get therapy sessions from yesterday
      const { count: therapyCount } = await supabase
        .from('therapy_messages')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('role', 'user')
        .gte('created_at', `${yesterday}T00:00:00`)
        .lt('created_at', `${yesterday}T23:59:59`);

      // Get wellness activities
      const { data: activities } = await supabase
        .from('wellness_activities')
        .select('activity_type, duration_minutes')
        .eq('user_id', userId)
        .gte('started_at', `${yesterday}T00:00:00`)
        .lt('started_at', `${yesterday}T23:59:59`);

      const meditationMinutes = activities
        ?.filter(a => a.activity_type === 'meditation')
        .reduce((sum, a) => sum + (a.duration_minutes || 0), 0) || 0;

      const topActivities = [...new Set(activities?.map(a => a.activity_type) || [])];

      // Get streak info
      const { data: streakData } = await supabase
        .from('nutrition_streaks')
        .select('current_streak')
        .eq('user_id', userId)
        .maybeSingle();

      return {
        checkInCompleted: !!checkIn,
        mood: checkIn?.daily_feeling || null,
        sleepHours: checkIn?.hours_slept || null,
        sleepQuality: checkIn?.sleep_quality || null,
        therapySessions: therapyCount || 0,
        meditationMinutes,
        streakCount: streakData?.current_streak || 0,
        topActivities: topActivities.slice(0, 3),
      };
    },
    enabled: !!userId && !alreadyShown,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const handleDismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
    onDismiss?.();
  };

  // Don't show if already dismissed, already shown today, or no data
  if (dismissed || alreadyShown || isLoading || !summary) {
    return null;
  }

  // Don't show if nothing happened yesterday
  const hasContent = summary.checkInCompleted || summary.therapySessions > 0 || summary.meditationMinutes > 0;
  if (!hasContent) {
    return null;
  }

  const moodInfo = summary.mood ? MOOD_LABELS[summary.mood] : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, height: 0 }}
        animate={{ opacity: 1, y: 0, height: 'auto' }}
        exit={{ opacity: 0, y: -20, height: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6"
      >
        <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-background border-primary/20 overflow-hidden">
          <CardContent className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">Welcome back!</h3>
                  <p className="text-xs text-muted-foreground">Here's how yesterday went</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={handleDismiss}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {/* Check-in Status */}
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                <CheckCircle2 className={`w-4 h-4 ${summary.checkInCompleted ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-xs">
                  <div className="font-medium text-foreground">{summary.checkInCompleted ? 'Checked in' : 'No check-in'}</div>
                  {moodInfo && (
                    <div className="text-foreground/70">{moodInfo.emoji} {moodInfo.label}</div>
                  )}
                </div>
              </div>

              {/* Sleep */}
              {summary.sleepHours && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                  <Moon className="w-4 h-4 text-primary" />
                  <div className="text-xs">
                    <div className="font-medium text-foreground">{summary.sleepHours}h sleep</div>
                    {summary.sleepQuality && (
                      <div className="text-foreground/70">Quality: {summary.sleepQuality}/10</div>
                    )}
                  </div>
                </div>
              )}

              {/* Therapy */}
              {summary.therapySessions > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                  <Brain className="w-4 h-4 text-primary" />
                  <div className="text-xs">
                    <div className="font-medium text-foreground">{summary.therapySessions} messages</div>
                    <div className="text-foreground/70">AI Reflection</div>
                  </div>
                </div>
              )}

              {/* Meditation */}
              {summary.meditationMinutes > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                  <Heart className="w-4 h-4 text-secondary" />
                  <div className="text-xs">
                    <div className="font-medium text-foreground">{summary.meditationMinutes} min</div>
                    <div className="text-foreground/70">Meditation</div>
                  </div>
                </div>
              )}

              {/* Streak */}
              {summary.streakCount > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                  <Flame className="w-4 h-4 text-accent" />
                  <div className="text-xs">
                    <div className="font-medium text-foreground">{summary.streakCount} day streak</div>
                    <div className="text-foreground/70">Keep going!</div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="flex items-center justify-between pt-2 border-t border-border/30">
              <p className="text-xs text-muted-foreground">
                Ready to continue your journey?
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-primary hover:text-primary/80"
                onClick={() => {
                  handleDismiss();
                  navigate('/daily-check-in');
                }}
              >
                Start today's check-in
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};
